import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import '../PolicyPage.css';

const Section = ({ title, children }) => (
  <section className="policy-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const MethodCard = ({ number, title, steps, cta }) => (
  <div className="policy-method">
    <div className="policy-method-header">
      <span className="policy-method-badge">방법 {number}</span>
      <h3 className="policy-method-title">{title}</h3>
    </div>
    <ol>
      {steps.map((step, index) => (
        <li key={index}>{step}</li>
      ))}
    </ol>
    {cta && <div className="policy-method-action">{cta}</div>}
  </div>
);

const DataGroupCard = ({ title, items }) => (
  <div className="policy-data-card">
    <h3>{title}</h3>
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
);

/**
 * Google Play 및 App Store 계정 삭제 정책을 위한 공개 안내 페이지.
 * 실제 탈퇴는 로그인 후 /subscription 또는 본인 이메일 요청으로 진행한다.
 */
const AccountDeletePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const buildMailtoUrl = () => {
    const subject = encodeURIComponent('[회원 탈퇴 요청] 왓퀴즈');
    const bodyLines = [
      '왓퀴즈 운영팀께,',
      '',
      '아래 정보로 회원 탈퇴를 요청합니다.',
      '',
      '· 가입 이메일: ',
      '· 가입 방법(Google / 이메일): ',
      '· 탈퇴 사유(선택): ',
      '',
      '관련 모든 개인정보를 즉시 영구 삭제하고, 처리 결과를 회신 부탁드립니다.',
      '',
      '※ 본인 확인을 위해 위 가입 이메일과 동일한 주소에서 보내주세요.',
    ];
    return `mailto:angel@plactical.com?subject=${subject}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
  };

  return (
    <>
      <Helmet>
        <title>회원 탈퇴 안내 | 왓퀴즈</title>
        <meta
          name="description"
          content="왓퀴즈 계정 삭제 절차, 삭제되는 데이터, 보관되는 데이터와 보관 기간을 안내합니다."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://whatquiz.co.kr/account/delete" />
      </Helmet>

      <main className="policy-page">
        <article className="policy-document policy-document--wide">
          <header className="policy-header">
            <p className="policy-eyebrow">계정 관리</p>
            <h1 className="policy-title">회원 탈퇴 안내</h1>
            <p className="policy-subtitle">
              계정을 삭제하는 방법과 함께 삭제·보관되는 데이터를 안내해요.
            </p>
          </header>

          <div className="policy-card policy-card--info">
            <p>
              본 페이지는 모바일 앱 / 웹 사용자가 회사 사이트(whatquiz.co.kr) 또는 모바일 앱에서
              계정을 삭제하는 절차와, 삭제·보관되는 데이터를 안내합니다.
            </p>
          </div>

          <Section title="1. 서비스 및 운영자 정보">
            <div className="policy-table-wrap">
              <table className="policy-table policy-table--key-value">
                <tbody>
                  <tr><th>서비스명</th><td>왓퀴즈 (whatquiz)</td></tr>
                  <tr><th>운영사</th><td>플랙티컬 주식회사</td></tr>
                  <tr>
                    <th>웹사이트</th>
                    <td><a href="https://whatquiz.co.kr">https://whatquiz.co.kr</a></td>
                  </tr>
                  <tr>
                    <th>고객 문의</th>
                    <td><a href="mailto:angel@plactical.com">angel@plactical.com</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="2. 회원 탈퇴 절차">
            <p>
              아래 세 가지 방법 중 하나로 회원 탈퇴를 요청할 수 있습니다.
              어떤 방법으로 요청하셔도 동일하게 모든 데이터가 삭제됩니다.
            </p>

            <MethodCard
              number="A"
              title="웹사이트에서 직접 탈퇴 (가장 빠름 · 자동 처리)"
              steps={[
                <>웹브라우저에서 <a href="https://whatquiz.co.kr">whatquiz.co.kr</a> 에 접속하여 로그인</>,
                <>상단 메뉴 → <strong>구독 / 마이페이지</strong> 진입 (또는 <a href="https://whatquiz.co.kr/subscription">/subscription</a> 직접 이동)</>,
                <>페이지 하단의 <strong>회원 탈퇴</strong> 버튼 클릭</>,
                <>안내 모달 확인 후 동의 → 탈퇴 완료</>,
              ]}
              cta={(
                <button
                  type="button"
                  className="policy-action"
                  onClick={() => navigate(currentUser ? '/subscription' : '/auth')}
                >
                  {currentUser ? '회원 탈퇴 화면으로 이동' : '로그인하고 탈퇴하기'}
                </button>
              )}
            />

            <MethodCard
              number="B"
              title="모바일 앱에서 직접 탈퇴 (Android / iOS)"
              steps={[
                <>왓퀴즈 앱 실행 후 로그인</>,
                <>하단 메뉴 또는 우측 상단 메뉴 → <strong>구독</strong> 진입</>,
                <>화면 하단 <strong>회원 탈퇴</strong> 버튼 → 안내 후 확인</>,
              ]}
            />

            <MethodCard
              number="C"
              title="이메일로 탈퇴 요청 (로그인 불가 시)"
              steps={[
                <>비밀번호 분실, 이메일 미수신, Google 계정 폐쇄 등으로 로그인이 어려운 경우 사용</>,
                <>아래 메일 주소로 <strong>가입 이메일과 동일한 주소</strong>에서 메일을 보내주세요</>,
                <>본인 확인 후 <strong>영업일 기준 3일 이내</strong> 처리 + 회신</>,
              ]}
              cta={(
                <a href={buildMailtoUrl()} className="policy-action">
                  탈퇴 요청 메일 보내기
                </a>
              )}
            />
          </Section>

          <Section title="3. 탈퇴 시 즉시 삭제되는 데이터">
            <p>
              회원 탈퇴 요청이 처리되면, 아래의 모든 정보가 <strong>회사 데이터베이스와 파일 저장소에서 즉시 영구 삭제</strong>되며 복구할 수 없습니다.
              제4절의 법정 보존 대상이 있는 경우에는 필요한 최소 거래 기록만 분리 보관합니다.
            </p>
            <div className="policy-data-grid">
              <DataGroupCard
                title="계정 정보"
                items={[
                  '이메일 주소(회원 ID)',
                  '이름, 프로필 사진(Google 연동 시)',
                  '학교/학년/담당 과목 등 프로필 데이터',
                  '연결된 Firebase Authentication 계정',
                ]}
              />
              <DataGroupCard
                title="이용자가 생성한 콘텐츠"
                items={[
                  'AI 채팅(대화) 기록과 첨부 파일',
                  '생성·저장한 생활기록부 초안 / 결과',
                  '생성한 평가지 / 학습지 / 평가계획서',
                  '업로드한 이미지·문서 파일',
                  '학급 명단(학생 이름·번호·이름 별칭)',
                  '학생 메모와 임원·진로희망',
                  '서술형 채점 기록과 학생 답안 파일',
                  '과제 제출방과 학생 제출물',
                  '커뮤니티 게시글, 댓글, 익명 매핑 정보',
                ]}
              />
              <DataGroupCard
                title="구독 / 결제 / 추천 정보"
                items={[
                  '구독 상태, 무료 체험 사용 이력',
                  'AI 토큰 사용 기록',
                  '쿠폰 발급/사용 이력',
                  '결제로 이어지지 않은 결제 시도 기록',
                  '토스페이먼츠 빌링키(자동결제 연결 정보)',
                  '추천인 코드 및 추천 보상 기록',
                  '연수 강사 조건확인 신청과 체결 전 서명',
                ]}
              />
              <DataGroupCard
                title="설정·캐시"
                items={[
                  '교사 환경설정 데이터',
                  '학생/클래스 정보 (교사 본인이 생성한 분)',
                  '서버 측 분석 캐시',
                  '로그인 세션 / 인증 토큰',
                ]}
              />
            </div>
          </Section>

          <Section title="4. 탈퇴 후에도 보관되는 데이터 및 보관 기간">
            <p>
              아래 항목은 <strong>관련 법령상의 의무</strong> 또는 <strong>부정 가입·악용 방지</strong>를 위해
              안전한 별도 저장소에 한시적으로 보관됩니다. 보관 기간이 지나면 자동으로 파기됩니다.
            </p>
            <div className="policy-table-wrap">
              <table className="policy-table">
                <thead>
                  <tr>
                    <th>보관 항목</th>
                    <th>보관 기간</th>
                    <th>법적 근거 / 사유</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>탈퇴 일시 + 이메일 해시</td><td><strong>30일</strong></td><td>부정한 재가입·악용 방지 (회사 내부 정책)</td></tr>
                  <tr><td>계약 / 청약철회 등에 관한 기록</td><td><strong>5년</strong></td><td>전자상거래 등에서의 소비자보호에 관한 법률 제6조</td></tr>
                  <tr><td>대금결제 및 재화 등의 공급에 관한 기록</td><td><strong>5년</strong></td><td>전자상거래 등에서의 소비자보호에 관한 법률 제6조</td></tr>
                  <tr><td>소비자 불만 또는 분쟁 처리에 관한 기록</td><td><strong>3년</strong></td><td>전자상거래 등에서의 소비자보호에 관한 법률 제6조</td></tr>
                  <tr><td>전자세금계산서 / 부가세 신고 자료</td><td><strong>5년</strong></td><td>국세기본법 제85조의3</td></tr>
                  <tr><td>체결된 연수 강사 계약·겸직 확인·정산 세무 기록</td><td><strong>최종 정산 후 5년</strong></td><td>계약·세무 분쟁 대응을 위한 내부 방침 및 관계 세법</td></tr>
                  <tr><td>접속 로그 / IP 주소</td><td><strong>3개월</strong></td><td>통신비밀보호법 제15조의2</td></tr>
                  <tr><td>익명화 / 비식별 통계 데이터</td><td>영구 보관</td><td>개인 식별이 불가능한 형태로 가공되어 서비스 개선·통계 목적에만 사용</td></tr>
                </tbody>
              </table>
            </div>
            <div className="policy-card policy-card--warning">
              <p>
                위 항목은 본인이 결제 / 청약철회 등 거래 이력이 있는 경우에만 해당됩니다.
                해당 이력이 없는 회원은 위 항목 또한 보관되지 않습니다.
              </p>
            </div>
          </Section>

          <Section title="5. 처리 소요 시간 및 재가입 제한">
            <ul>
              <li><strong>웹사이트 / 앱에서 직접 탈퇴</strong>: 데이터량에 따라 통상 수 분 이내 처리</li>
              <li><strong>이메일 요청</strong>: 영업일 기준 최대 3일 이내 처리</li>
              <li><strong>재가입 제한</strong>: 부정 사용 방지를 위해 탈퇴 후 <strong>30일간 동일 이메일로 재가입이 불가</strong>합니다.</li>
              <li><strong>30일 경과 후</strong>: 재가입 제한 기록도 자동 파기되며, 이후 가입에는 그 시점의 신규 가입 정책이 적용됩니다.</li>
              <li><strong>처리 결과 안내</strong>: 직접 탈퇴가 완료되면 가입 이메일로 데이터 파기 결과를 다시 안내합니다.</li>
            </ul>
          </Section>

          <Section title="6. 일부 데이터만 삭제하고 싶다면">
            <p>
              계정은 유지하면서 특정 콘텐츠(생기부 초안, 채팅 기록 등)만 삭제하고 싶다면 회원 탈퇴를 하지 않고
              아래 방법을 사용해 주세요.
            </p>
            <ul>
              <li>각 콘텐츠 화면의 <strong>휴지통 / 삭제</strong> 아이콘으로 개별 삭제</li>
              <li>구체적인 항목 삭제가 필요하면 <a href="mailto:angel@plactical.com">angel@plactical.com</a> 으로 요청</li>
            </ul>
          </Section>

          <footer className="policy-footer">
            <p>
              본 페이지는 Google Play Console 및 Apple App Store 의 계정 삭제 정책 요건을 충족하기 위해 제공됩니다.
              개인정보 처리에 대한 전반적 사항은{' '}
              <a href="/privacy-policy">개인정보처리방침</a>,{' '}
              <a href="/terms-of-service">이용약관</a>을 참조해 주세요.
            </p>
            <p>최종 갱신일: 2026년 10월 18일</p>
          </footer>
        </article>
      </main>
    </>
  );
};

export default AccountDeletePage;
